import { useCallback, useState, useEffect, useRef, useMemo } from 'react'

//* HOC's
import { withUIContext, withDataContext } from 'context'

//* Components
import Image from 'components/common/Image'
import Video from 'components/common/Video'
import Text from 'components/common/Text'
import Button from 'components/common/Button'
import Paths from 'components/common/Paths'
import SharePopup from 'components/common/Popups/SharePopup'
import Animate from 'components/common/Animate'

//* Style
import HeroStyle from './style'

const Hero = ({ file, title, subtitle, paths, openPopup, pageId, type, addBookmark, checkIsBookmarked, removeBookmark }) => {
    //! Refs
    const pathsRef = useRef()
    const titleRef = useRef()
    const subtitleRef = useRef()
    const buttonWrapRef = useRef()

    //! States
    const [isSaved, setIsSaved] = useState(false)

    //! Open Share
    const openSharePopup = useCallback(() => {
        openPopup(<SharePopup />)
    }, [openPopup])

    //! Check Is Saved
    useEffect(() => {
        setIsSaved(checkIsBookmarked({ type, id: pageId }))
    }, [])

    //! Handle Save Button Click
    const handleClickSaveButton = useCallback(() => {
        isSaved ? removeBookmark({ type, id: pageId }) : addBookmark({ type, id: pageId })

        setIsSaved(checkIsBookmarked({ type, id: pageId }))
    }, [type, pageId, isSaved])

    const pathAnimate = useMemo(() => {
        return <Animate duration={1.3} startY={'400%'} params={{ bottomToTop: [pathsRef] }} />
    }, [])

    const titleAnimate = useMemo(() => {
        return <Animate duration={1.3} params={{ bottomToTop: [titleRef] }} />
    }, [])

    const buttonsAnimate = useMemo(() => {
        return <Animate duration={1.3} startY={'300%'} params={{ bottomToTop: [buttonWrapRef] }} />
    }, [])

    const subtitleAnimate = useMemo(() => {
        return <Animate duration={1.3} params={{ bottomToTop: [subtitleRef] }} />
    }, [])

    return (
        <HeroStyle>
            {file.type === 'video' ? (
                <Video play={true} autoplay={true} muted={true} loop={true} controls={false} className={'hero-image'} src={file.url} />
            ) : (
                <Image className={'hero-image'} objectFit={'cover'} src={file.url} priority={true} />
            )}
            <div className='layer' />
            <div className='hero-content'>
                <div>
                    {paths && pathAnimate}
                    <div className='hidden-container'>
                        <Paths ref={pathsRef} isHero={true} paths={paths} pathColor={'primaryColor3'} />
                    </div>

                    {title && titleAnimate}
                    <div className='hidden-container'>
                        {title && <Text className={'h1 font-ping-regular primaryColor3 title'} text={title} ref={titleRef} />}
                    </div>
                </div>
                <div className='hero-footer hidden-container'>
                    <div className='hero-btns-wrap' ref={buttonWrapRef}>
                        {buttonsAnimate}
                        <Button
                            icon={'share'}
                            text={'share'}
                            className='hero-btn'
                            color={'ColorBigStone'}
                            background={'ColorWhite'}
                            borderColor={'transparent'}
                            hoverBorderColor={'transparent'}
                            hoverBackground={'ColorBigStone'}
                            hoverColor={'ColorWhite'}
                            onClick={openSharePopup}
                        />

                        <Button
                            icon={'bookmarks'}
                            text={isSaved ? 'saved' : 'save'}
                            className='hero-btn'
                            color={isSaved ? 'ColorBigStone' : 'ColorWhite'}
                            background={isSaved ? 'ColorWhite' : 'transparent'}
                            borderColor={'ColorWhite'}
                            hoverColor={'ColorBigStone'}
                            hoverBorderColor={'ColorWhite'}
                            hoverBackground={'ColorWhite'}
                            onClick={handleClickSaveButton}
                        />
                    </div>
                    <div>
                        {subtitle && subtitleAnimate}
                        <Text className={'h1 font-soundscape-bold primaryColor3 uppercase subtitle'} ref={subtitleRef} text={subtitle} />
                    </div>
                </div>
            </div>
        </HeroStyle>
    )
}

export default withUIContext(withDataContext(Hero, ['addBookmark', 'checkIsBookmarked', 'removeBookmark']), ['openPopup'])
